import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { trackEvent, trackCartAdd } from '@/lib/analytics';
import { getCartSessionId, rotateCartSessionId } from '@/lib/cartSession';
import type { CartItem, CustomResponse } from '@shared/schema';

function pathNow(): string | undefined {
  return typeof window !== 'undefined' ? window.location.pathname : undefined;
}

const CART_KEY_PREFIX = 'marketlink.cart.';
const LEGACY_CART_KEY = 'marketplace_cart';

function storageKeyFor(userId: string | null | undefined): string | null {
  return userId ? `${CART_KEY_PREFIX}${userId}` : null;
}

// The cart is per-user and deliberately gated behind an account (see
// useGuestBrowsing.ts — Apple 5.1.1(v) permits requiring registration for cart
// and checkout). A logged-out tap on "Add to cart" used to flip the button to
// "Added" and then silently drop the item, because storageKeyFor(null) is null
// so nothing was ever persisted. Instead we park the intent here, send them to
// sign in, and replay it once they're authenticated.
const PENDING_ADD_KEY = 'marketlink.pendingCartAdd';

export type PendingCartAdd = {
  itemId: string;
  qty: number;
  maxQty?: number;
  requestText?: string;
  customResponses?: CustomResponse[];
  weightUnit?: { id: string; weight: number; priceCents: number };
  returnTo?: string;
};

export function readPendingCartAdd(): PendingCartAdd | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PENDING_ADD_KEY);
    return raw ? (JSON.parse(raw) as PendingCartAdd) : null;
  } catch {
    return null;
  }
}

export function clearPendingCartAdd(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(PENDING_ADD_KEY);
  } catch { /* private mode */ }
}

// Module scope so the replay happens once across every mounted useCart(), not
// once per instance — Header and the page each hold their own copy.
let pendingReplayDone = false;

export function useCart() {
  const { user, isLoading: authLoading } = useAuth();
  const userId = (user as { id?: string } | null | undefined)?.id ?? null;
  const [cart, setCart] = useState<CartItem[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (authLoading) return;
    try {
      localStorage.removeItem(LEGACY_CART_KEY);
    } catch { /* private mode / quota */ }

    const key = storageKeyFor(userId);
    if (!key) {
      setCart([]);
      setHydrated(true);
      return;
    }
    try {
      const stored = localStorage.getItem(key);
      setCart(stored ? JSON.parse(stored) : []);
    } catch {
      setCart([]);
    }
    setHydrated(true);
  }, [userId, authLoading]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!hydrated) return;
    const key = storageKeyFor(userId);
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(cart));
    } catch { /* quota */ }
  }, [cart, userId, hydrated]);

  const addToCart = (
    itemId: string,
    qty: number = 1,
    maxQty?: number,
    requestText?: string,
    customResponses?: CustomResponse[],
    weightUnit?: { id: string; weight: number; priceCents: number },
  ) => {
    if (!userId) {
      try {
        localStorage.setItem(
          PENDING_ADD_KEY,
          JSON.stringify({
            itemId,
            qty,
            maxQty,
            requestText,
            customResponses,
            weightUnit,
            returnTo: typeof window !== 'undefined' ? window.location.pathname : undefined,
          } satisfies PendingCartAdd),
        );
      } catch { /* private mode — they'll just re-add after signing in */ }
      trackEvent('cart_add_signin_required', pathNow(), { itemId });
      // Full load rather than a wouter push: this is an auth transition and the
      // pending intent is already durable in localStorage.
      if (typeof window !== 'undefined') window.location.assign('/login');
      return;
    }
    trackCartAdd('cart_add', pathNow(), {
      cartSessionId: getCartSessionId(userId),
      itemId,
      qtyAdded: qty,
      weightUnitId: weightUnit?.id,
      unitPriceCents: weightUnit?.priceCents,
      maxQty,
    });
    setCart(prev => {
      // A sold-by-weight line is one specific reserved unit, qty fixed at 1.
      // Re-picking a size for the same tier replaces the prior unit.
      if (weightUnit) {
        // Each weight unit is a distinct physical item; allow several per tier,
        // keyed by unit id. The same reserved unit can't be added twice.
        if (prev.some(item => item.weightUnitId === weightUnit.id)) return prev;
        return [
          ...prev,
          {
            itemId,
            qty: 1,
            weightUnitId: weightUnit.id,
            unitWeightCentiLb: weightUnit.weight,
            unitPriceCents: weightUnit.priceCents,
          },
        ];
      }
      const existing = prev.find(item => item.itemId === itemId);
      const currentQty = existing?.qty ?? 0;
      let nextQty = currentQty + qty;
      if (typeof maxQty === 'number' && nextQty > maxQty) {
        nextQty = Math.max(currentQty, maxQty);
      }
      if (existing) {
        const nextRequest = requestText !== undefined ? requestText : existing.requestText;
        const nextResponses =
          customResponses !== undefined ? customResponses : existing.customResponses;
        if (
          nextQty === existing.qty &&
          nextRequest === existing.requestText &&
          nextResponses === existing.customResponses
        )
          return prev;
        return prev.map(item =>
          item.itemId === itemId
            ? { ...item, qty: nextQty, requestText: nextRequest, customResponses: nextResponses }
            : item
        );
      }
      if (nextQty <= 0) return prev;
      return [
        ...prev,
        {
          itemId,
          qty: nextQty,
          ...(requestText !== undefined ? { requestText } : {}),
          ...(customResponses !== undefined ? { customResponses } : {}),
        },
      ];
    });
  };

  // Replay the add that sent them to sign in, once the cart has hydrated under
  // their user key so the item merges into whatever was already there.
  useEffect(() => {
    if (!hydrated || !userId || pendingReplayDone) return;
    const pending = readPendingCartAdd();
    if (!pending) return;
    pendingReplayDone = true;
    clearPendingCartAdd();
    addToCart(
      pending.itemId,
      pending.qty,
      pending.maxQty,
      pending.requestText,
      pending.customResponses,
      pending.weightUnit,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, userId]);

  const updateQuantity = (itemId: string, qty: number) => {
    if (qty <= 0) {
      removeFromCart(itemId);
      return;
    }
    setCart(prev =>
      prev.map(item =>
        item.itemId === itemId ? { ...item, qty } : item
      )
    );
  };

  const updateRequestText = (itemId: string, requestText: string) => {
    setCart(prev =>
      prev.map(item =>
        item.itemId === itemId ? { ...item, requestText } : item
      )
    );
  };

  const updateCustomResponses = (itemId: string, customResponses: CustomResponse[]) => {
    setCart(prev =>
      prev.map(item =>
        item.itemId === itemId ? { ...item, customResponses } : item
      )
    );
  };

  const removeFromCart = (itemId: string, weightUnitId?: string) => {
    trackEvent('cart_remove', pathNow(), {
      cartSessionId: getCartSessionId(userId),
      itemId,
      weightUnitId,
    });
    if (weightUnitId) {
      // Free the reserved bird so it's available to other buyers again.
      fetch("/api/cart/release-weight-unit", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ unitId: weightUnitId }),
      }).catch(() => {});
    }
    setCart(prev =>
      prev.filter(item =>
        weightUnitId ? item.weightUnitId !== weightUnitId : item.itemId !== itemId,
      ),
    );
  };

  const clearCart = () => {
    setCart([]);
    rotateCartSessionId(userId);
    const key = storageKeyFor(userId);
    if (key && typeof window !== 'undefined') {
      try {
        localStorage.removeItem(key);
      } catch { /* quota */ }
    }
  };

  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  return {
    cart,
    // Non-null only when there's something to attribute; avoids minting an id
    // for users who open checkout with an empty cart.
    cartSessionId: cart.length > 0 ? getCartSessionId(userId) : null,
    addToCart,
    updateQuantity,
    updateRequestText,
    updateCustomResponses,
    removeFromCart,
    clearCart,
    cartCount,
    hydrated,
  };
}
